// =============================================================================
// Shopify Storefront API client (client-side, PUBLIC token only)
// -----------------------------------------------------------------------------
// The website design/data stays local (see src/data/catalog.js). Shopify is
// used ONLY for cart + checkout: each local product/bundle maps to a Shopify
// variant, and checkout builds a Shopify cart and redirects to Shopify's
// hosted checkout.
// =============================================================================

const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const API_VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || "2024-10";
const ENDPOINT = `https://${DOMAIN}/api/${API_VERSION}/graphql.json`;

// Map local site product/bundle ids -> Shopify ProductVariant GIDs.
// (Resolved from the Storefront API by handle.)
export const SHOPIFY_VARIANTS = {
  // Core products
  "baby-hemp-calm-balm-stick": "gid://shopify/ProductVariant/53998346568026", // calm-balm-stick
  "baby-hemp-eczema-patches": "gid://shopify/ProductVariant/54380841828698", // baby-hemp-eczema-patches-pack-of-10
  "baby-hemp-patch-balm-bundle": "gid://shopify/ProductVariant/54380860047706", // baby-hemp-patch-balm-bundle
  // Bundle builder options (ids are prefixed with "bundle-" when added to cart)
  "bundle-balm-only": "gid://shopify/ProductVariant/53998346568026",
  "bundle-patch-only": "gid://shopify/ProductVariant/54380841828698",
  "bundle-patch-balm": "gid://shopify/ProductVariant/54380860047706",
  "bundle-3-pack": "gid://shopify/ProductVariant/54380874432858",
  "bundle-6-pack": "gid://shopify/ProductVariant/54380869321050",
  "bundle-family": "gid://shopify/ProductVariant/54380881772890",
};

export const getVariantId = (siteId) => SHOPIFY_VARIANTS[siteId] || null;

// Shopify Subscribe & Save selling plans (shared group "Baby Hemp Subscribe & Save").
// Site interval id -> Shopify SellingPlan GID.
export const SELLING_PLANS = {
  "2w": "gid://shopify/SellingPlan/692203028826",
  "4w": "gid://shopify/SellingPlan/692203061594",
  "6w": "gid://shopify/SellingPlan/692203094362",
  "8w": "gid://shopify/SellingPlan/692203127130",
};

// Variants that are enrolled in the subscription plan (patches + bundles, NOT the balm).
export const SUBSCRIPTION_VARIANTS = new Set([
  "gid://shopify/ProductVariant/54380841828698", // eczema patches
  "gid://shopify/ProductVariant/54380860047706", // patch + balm bundle
  "gid://shopify/ProductVariant/54380874432858", // 3 pack
  "gid://shopify/ProductVariant/54380869321050", // 6 pack
  "gid://shopify/ProductVariant/54380881772890", // family care
]);

async function graphql(query, variables = {}) {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0]?.message || "Shopify error");
  return json.data;
}

const CART_CREATE = `
  mutation CartCreate($lines: [CartLineInput!]!) {
    cartCreate(input: { lines: $lines }) {
      cart { id checkoutUrl }
      userErrors { field message }
    }
  }
`;

// Build a Shopify cart from local cart items and return the hosted checkout URL.
export async function createShopifyCheckout(items) {
  if (!DOMAIN || !TOKEN) {
    throw new Error("Shopify is not configured (missing store domain or Storefront token).");
  }

  const mappable = items.filter((i) => i.variantId);
  if (mappable.length !== items.length) {
    const unmapped = items.filter((i) => !i.variantId).map((i) => i.id);
    console.warn("[shopify] Cart items without a Shopify variant mapping were skipped:", unmapped);
  }

  const lines = mappable.map((i) => {
    const line = { merchandiseId: i.variantId, quantity: i.qty };
    // Attach the Shopify selling plan for Subscribe & Save (eligible variants only).
    if (i.sellingPlan === "subscribe" && SUBSCRIPTION_VARIANTS.has(i.variantId)) {
      const planId = SELLING_PLANS[i.interval];
      if (planId) line.sellingPlanId = planId;
    }
    return line;
  });

  if (lines.length === 0) {
    throw new Error("No Shopify products in cart");
  }

  const data = await graphql(CART_CREATE, { lines });
  const result = data.cartCreate;
  if (result.userErrors && result.userErrors.length) {
    throw new Error(result.userErrors[0].message);
  }
  const url = result.cart?.checkoutUrl;
  if (!url) throw new Error("Could not create checkout");
  return url;
}
